import type { WebTelemetryConfig } from '@ztechapps/common.contract'
import { getWindow, hasWindow } from '@ztechapps/common.utils'

// Shape of the window.__ENV__ payload's `app` key — same flat, camelCase
// convention as apps/panelpunches/admin/src/config/app-settings.ts. Client-visible
// values only (public URLs/keys); secrets never leave the backend. Deliberately
// decoupled from `Setting.appSettings` below (the exported, nested public shape
// consumers import) — this is only the wire shape for window.__ENV__.
type RuntimeAppEnv = {
  supportEmail?: string
  recaptchaSiteKey?: string
  storeUrl?: string
  apiUrl?: string
  stripePublishableKey?: string
}
type RuntimeTelemetry = { serviceName?: string; enabled?: boolean; sampleRate?: number }
type RuntimeEnv = {
  version?: string
  app?: RuntimeAppEnv
  telemetry?: RuntimeTelemetry
}

const isServer = import.meta.env.SSR

// Server-side (this module runs inside the Vite SSR bundle, imported once by
// `server/index.ts` per container boot): read `process.env` directly so the
// values reflect THIS container's env, set at runtime by CapRover — not
// `import.meta.env.VITE_*`, which Vite bakes into the SSR bundle as literal
// strings at `vite build` time and would be identical for every deploy that
// reuses the same built image (beta and production alike).
// Client-side: read the payload the server injected into `window.__ENV__` for
// this request, built from the same `process.env` read above. Both the dev
// server and production go through this SSR render, so there is no build-time
// fallback anywhere — the built artifact carries no environment values.
const runtimeEnv: RuntimeEnv | undefined = isServer
  ? {
      app: {
        supportEmail: process.env.VITE_SUPPORT_EMAIL,
        recaptchaSiteKey: process.env.VITE_GOOGLE_RECAPTCHA_SITEKEYS,
        storeUrl: process.env.VITE_APP_STORE_URL,
        apiUrl: process.env.VITE_APP_API_URL,
        stripePublishableKey: process.env.VITE_STRIPE_PUBLISHABLEKEY,
      },
      telemetry: {
        serviceName: process.env.VITE_OTEL_SERVICE_NAME,
        enabled: process.env.VITE_OTEL_ENABLED === 'true',
        sampleRate: Number(process.env.VITE_OTEL_SAMPLE_RATE) || undefined,
      },
    }
  : hasWindow()
    ? (getWindow() as unknown as { __ENV__?: RuntimeEnv }).__ENV__
    : undefined

const app = runtimeEnv?.app

// Public shape kept stable for existing consumers (e.g. `appSettings.AppUrl.api`,
// `appSettings.Google.recaptchaSiteKey`) — only the internal runtime source
// (`app`, above) changed, not this exported object's fields.
export const Setting = {
  version: '3.0.0',
  serverSide: isServer,
  appSettings: {
    supportEmail: app?.supportEmail,
    RootUrl: app?.storeUrl,
    Google: {
      recaptchaSiteKey: app?.recaptchaSiteKey,
    },
    Stripe: {
      publishableKey: app?.stripePublishableKey,
    },
    AppUrl: {
      store: app?.storeUrl,
      api: app?.apiUrl,
    },
  },
}

export const appSettings = Setting.appSettings

// Injected into window.__ENV__ by entry-server.tsx; also the shape `runtimeEnv`
// (above) expects to read back on the client, so server and client stay in sync.
// Remaps the exported (nested) `appSettings` back into the flat `app` wire shape.
export const clientAppSettings: RuntimeEnv = {
  version: Setting.version,
  app: {
    supportEmail: appSettings.supportEmail,
    recaptchaSiteKey: appSettings.Google.recaptchaSiteKey,
    storeUrl: appSettings.AppUrl.store,
    apiUrl: appSettings.AppUrl.api,
    stripePublishableKey: appSettings.Stripe.publishableKey,
  },
  telemetry: runtimeEnv?.telemetry,
}

/**
 * OpenTelemetry configuration for frontend telemetry
 */
export const telemetryConfig: WebTelemetryConfig = {
  appName: 'store',
  serviceName: clientAppSettings.telemetry?.serviceName,
  collectorUrl: '/otel',
  enabled: clientAppSettings.telemetry?.enabled ?? false,
  sampleRate: clientAppSettings.telemetry?.sampleRate ?? 0.05, // Sample rate configurable via env
  debug: import.meta.env.DEV, // Enable debug logging only in development
  serviceVersion: Setting.version,
}
